import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useLoad } from "../../../shared/loading/useLoad";
import { appPaths } from "../../../shared/routing/appPaths";
import { connectionStatus, latestUpdateRequest } from "../connections/connectionState";
import { listSourceConnections } from "../connections/sourceConnectionsApi";

/** How often the form looks whether the import's Thread has started. */
const POLL_MILLISECONDS = 3000;

/**
 * The import as a Workflow run (F16): waits for the Thread the source's connection starts
 * for the new commit, and hands it to `onStarted` as soon as it exists, so the preparation
 * turns into that Thread instead of making a second one (WA-12). `commit` `null` follows the
 * connection's newest request, as after a Cloud repository's first fetch.
 */
export default function ImportRunStatus({ projectId, sourceId, commit, onStarted }: { projectId: string; sourceId: string; commit: string | null; onStarted: (threadId: string) => void }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listSourceConnections(projectId, signal), [projectId]);
  const { state, refresh } = useLoad(load);
  const view = state.status === "loaded" ? state.value.find((candidate) => candidate.connection.sourceId === sourceId) ?? null : null;
  const request = view === null ? null : commit === null ? latestUpdateRequest(view.connection) : view.connection.requests.find((candidate) => candidate.newCommit === commit) ?? null;
  const thread = request?.thread ?? null;
  const settled = state.status === "failed" || thread !== null || request?.state === "failed";
  useEffect(() => {
    if (settled) return undefined;
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") refresh(); }, POLL_MILLISECONDS);
    return () => { window.clearInterval(timer); };
  }, [refresh, settled]);
  // Once per Thread: the caller may pass a new handler with each render.
  const handedOver = useRef<string | null>(null);
  useEffect(() => {
    if (thread !== null && handedOver.current !== thread) {
      handedOver.current = thread;
      onStarted(thread);
    }
  }, [thread, onStarted]);

  if (view === null || request === null || thread === null) {
    const waiting = state.status === "failed" ? "unknown" : request?.state === "failed" ? "failed" : "starting";
    return <p role="status" className="app-help-text">{t(`sources.import.run.${waiting}`)}</p>;
  }
  const status = commit === null || request.newCommit === view.connection.upstream ? connectionStatus(view.connection, view.thread) : null;
  return (
    <p role="status">
      {t("sources.import.run.started")}{" "}
      <Link className="app-link font-medium underline" to={appPaths.thread(thread)}>{t("sources.import.run.open")}</Link>
      {status === null ? null : <span className="app-text-muted"> · {t(`sources.connections.states.${status}`)}</span>}
    </p>
  );
}
